import type { NavGuard } from '../generated/nav-tree'
import type { ModuleRoute, RouteGuard } from '../module-contract'
import { canAccess, type AccessContext } from './access'

/**
 * Whether a sidebar item is offered — FRONTEND.md §9.224, FB-370.
 *
 * **The sidebar and the router read different data about the same page.** The router reads
 * `ModuleRoute.guard`, which FB-001 corrected to mirror what the server enforces. The sidebar reads
 * the guard its item was generated with, which for the 55 items out of `manager-nav.json` is
 * *legacy's* nav guard. Where those disagree the sidebar offers a link that ends in `RouteGate`'s
 * lock panel — five of them on the owner's real `role_permissions`.
 *
 * **Both guards apply, and neither replaces the other.** The route guard carries the role gate the
 * nav guard lacks; the nav guard carries the `franchiseModule` tier gate the route guard usually
 * lacks — `/settings-backups` is gated on the `backups` module in the nav and on nothing in the
 * route. Taking the route's guard alone would offer a franchise a page outside its package, which is
 * the same class of bug pointing the other way.
 */

/** The guard the router will apply at each path. */
export function routeGuardsByPath(routes: readonly ModuleRoute[]): Map<string, RouteGuard> {
  const guards = new Map<string, RouteGuard>()
  for (const route of routes) {
    if (route.guard) guards.set(route.path, route.guard)
  }
  return guards
}

/**
 * An item is shown only when the sidebar's own gate **and** the router's gate both admit the user.
 *
 * A path with no route of its own — a section heading's target, an external link — keeps its nav
 * guard alone, because there is no second opinion to take. This is deliberately not a fallback to
 * *deny*: hiding an item because no route matched would blank parts of the sidebar the moment a path
 * were spelled differently in the two files.
 */
export function navItemVisible(
  item: { path: string; guard?: NavGuard | RouteGuard },
  routeGuards: Map<string, RouteGuard>,
  access: AccessContext,
): boolean {
  if (!canAccess(item.guard, access)) return false

  const routeGuard = routeGuards.get(item.path)
  if (routeGuard === undefined) return true

  return canAccess(routeGuard, access)
}

/**
 * Whether the route served at `path` opens for this person - FRONTEND.md §9.759, FB-1078.
 *
 * For a link outside the sidebar that goes straight to a route: the header's *Rozvoz* and *Nástenka*. It reads the
 * router's own guard by path, so the link is offered to exactly whom `RouteGate` admits. They asked `surfacesFor(role)`
 * until then - a third answer beside the sidebar's and the router's, which offered *Rozvoz* to every administrator
 * while the route refused them.
 *
 * **The routes rather than `routeGuardsByPath`'s map**, because the map keeps only guarded routes: `/kitchen` carries
 * no guard (FB-002) and would read the same as a path nothing serves. **A path no route serves answers false** - a
 * link to nothing is not one to offer, and a renamed route loses its header link rather than keeping a dead one.
 */
export function routeOpensFor(
  path: string,
  routes: readonly Pick<ModuleRoute, 'path' | 'guard'>[],
  access: AccessContext,
): boolean {
  const route = routes.find((candidate) => candidate.path === path)
  return route !== undefined && canAccess(route.guard, access)
}

/** A sidebar section as far as visibility is concerned: its own gate and its entries'. */
interface GatedSection {
  guard?: NavGuard | RouteGuard | undefined
  items: ReadonlyArray<{ path: string; guard?: NavGuard | RouteGuard | undefined }>
}

/**
 * Whether the sidebar draws the entry at `path` at all - §9.674, owner O58.
 *
 * **The section's gate and then `navItemVisible`, which are the two questions `SidebarSection` asks before it
 * draws an entry.** Something that decorates an entry from outside the sidebar - the red count of unaccepted
 * orders on *Rozvoz - nástenka*, and its copy in the header - asks this instead of restating the entry's guard,
 * so it cannot come to be shown, or to poll, for somebody the sidebar would not show the entry to.
 *
 * **A path no section carries answers false**, which is the safe direction: a renamed entry loses its badge
 * rather than turning on a read for everybody.
 */
export function navEntryVisible(
  path: string,
  sections: readonly GatedSection[],
  routeGuards: Map<string, RouteGuard>,
  access: AccessContext,
): boolean {
  return sections.some(
    (section) =>
      canAccess(section.guard, access) &&
      section.items.some((item) => item.path === path && navItemVisible(item, routeGuards, access)),
  )
}
